// hooks/useReferral.ts - Simplified version
import { useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation, NavigateOptions } from 'react-router-dom';

type NavigateTarget = string | number | { pathname: string; search?: string; hash?: string; state?: any };

export const useReferral = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Only get referral from URL, not from storage for auto-persistence
  const referralCode = searchParams.get('referral');

  // Only save to storage when URL has referral (no auto-read from storage)
  useEffect(() => {
    if (referralCode) {
      sessionStorage.setItem('referral_code', referralCode);
    }
  }, [referralCode]);

  const navigateWithReferral = (to: NavigateTarget, options?: NavigateOptions) => {
    if (typeof to === 'number') {
      navigate(to);
      return;
    }

    // Only add referral if it exists in current URL
    if (!referralCode) {
      navigate(to as any, options);
      return;
    }

    if (typeof to === 'string' && !to.startsWith('/')) {
      const currentPath = location.pathname.endsWith('/') 
        ? location.pathname 
        : location.pathname + '/';
      const absolutePath = new URL(to, `http://localhost${currentPath}`).pathname;
      
      const newSearchParams = new URLSearchParams();
      newSearchParams.set('referral', referralCode);
      
      navigate(`${absolutePath}?${newSearchParams.toString()}`, options);
      return;
    }

    if (typeof to === 'string') {
      const url = new URL(to, window.location.origin);
      const newSearchParams = new URLSearchParams(url.search);
      newSearchParams.set('referral', referralCode);
      navigate(`${url.pathname}?${newSearchParams.toString()}`, options);
      return;
    }

    if (typeof to === 'object' && to !== null) {
      const currentSearchParams = new URLSearchParams(to.search || '');
      currentSearchParams.set('referral', referralCode);
      
      const newLocation = {
        ...to,
        search: `?${currentSearchParams.toString()}`
      };
      
      navigate(newLocation, options);
      return;
    }

    navigate(to as any, options);
  };

  const clearReferral = () => {
    sessionStorage.removeItem('referral_code');
    if (searchParams.get('referral')) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('referral');
      navigate(`?${newSearchParams.toString()}`, { replace: true });
    }
  };

  return { referralCode, navigateWithReferral, clearReferral };
};